import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {ActivityLogsGetListSchema, ActivityLogsStatuses} from '../../../api/DBSchema/ActivityLogs';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import store from '../../../store';
import canceledLogo from '../../../assets/images/log_canceled.png';
import doneLogo from '../../../assets/images/log_done.png';
import errorLogo from '../../../assets/images/log_error.png';
import runningLogo from '../../../assets/images/log_running.png';
import waitingLogo from '../../../assets/images/log_waiting.png';
import {dateAndTimeToString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {useSnackbar} from 'notistack';
import LogsToExcelSnackbarContent from '../../PopupPages/LogsToExcelSnackbarContent';
import SMCLink from '../../../components/Simple/SMCLink';
import { buildNumberSort, buildStringSortCaseInsensitive, buildWeighedSort } from '../../../utils/sortUtils';

function getCellColor(cell) {
    const logSeverity = cell[ActivityLogsGetListSchema.status];
    let cellColor;
    if (logSeverity === ActivityLogsStatuses.done) {
        cellColor = 'green';
    }
    if (logSeverity === ActivityLogsStatuses.error) {
        cellColor = 'red';
    }
    if (logSeverity === ActivityLogsStatuses.running) {
        cellColor = '#CC6600';
    }
    if (logSeverity === ActivityLogsStatuses.waiting) {
        cellColor = '#CC6600';
    }
    if (logSeverity === ActivityLogsStatuses.canceled) {
        cellColor = 'green';
    }

    return cellColor;
}
const statusCell = React.memo(({value}) => {
    const { t } = useTranslation();

    switch (value) {
        case ActivityLogsStatuses.done:
            return <img src={doneLogo} title={t('SMC_LOGSTASKS_STATUS_DONE')} alt="done" />;
        case ActivityLogsStatuses.error:
            return <img src={errorLogo} title={t('SMC_LOGSTASKS_STATUS_ERROR')} alt="error" />;
        case ActivityLogsStatuses.running:
            return <img src={runningLogo} title={t('SMC_LOGSTASKS_STATUS_RUN')} alt="running" />;
        case ActivityLogsStatuses.waiting:
            return <img src={waitingLogo} title={t('SMC_LOGSTASKS_STATUS_WAIT')} alt="waiting" />;
        case ActivityLogsStatuses.canceled:
            return <img src={canceledLogo} title={t('SMC_LOGSTASKS_STATUS_CANCELED')} alt="canceled" />;
        default:
            console.error("Activity logs table - status " + value + " not found icon")
            return null;
    }
});

const statusWeights = {
    [ActivityLogsStatuses.done]: 0,
    [ActivityLogsStatuses.error]: 1,
    [ActivityLogsStatuses.waiting]: 2,
    [ActivityLogsStatuses.running]: 3,
    [ActivityLogsStatuses.canceled]: 4
}

const dateCell = React.memo(({value, object}) => {
    if (value) {
        return (
            <span style={{ fontSize: '8pt', color: getCellColor(object) }}>{dateAndTimeToString(value, true)}</span>
        );
    }
    return null;
});

const targetCell = React.memo(({value, object}) => {
    const { t } = useTranslation();
    if (!value) {
        return null;
    }
    let title;
    let basePath;
    switch (value.type) {
        case ActivityLogsGetListSchema.targetSchema.typeValues.gateways:
            title = t('SMC_GATEWAYSVIEW_EDIT_TIP');
            basePath = SMCPaths.gateways;
            break;
        case ActivityLogsGetListSchema.targetSchema.typeValues.users:
            title = t('SMC_USERSVIEW_EDIT_TIP');
            basePath = SMCPaths.users;
            break;
        case ActivityLogsGetListSchema.targetSchema.typeValues.administrators:
            title = t('SMC_GLOBAL_ADMINS_EDIT_TIP');
            basePath = SMCPaths.administrators;
            break;
        case ActivityLogsGetListSchema.targetSchema.typeValues.communities:
            title = t('SMC_COMMUNITIES_EDIT_TIP');
            basePath = SMCPaths.communities;
            break;
        case ActivityLogsGetListSchema.targetSchema.typeValues.servers:
            title = t('SMP.SERVER_ADDRESS_WIZ.EDIT_SERVER_ADDRESS');
            basePath = SMCPaths.servers;
            break;
        default:
            title = null;
            basePath = null;
            break;
    }
    if (basePath) {
        return (
            <SMCLink
                style={{ fontSize: '8pt', color: getCellColor(object) }}
                title={title}
                onClick={() => store.dispatch(navigateTo(basePath+'?id='+value[ActivityLogsGetListSchema.targetSchema.value]))}
                underline
            >
                {value[ActivityLogsGetListSchema.targetSchema.displayValue]}
            </SMCLink>
        );
    }
    else {
        return (
            <span style={{ fontSize: '8pt', color: getCellColor(object) }}>
                {value[ActivityLogsGetListSchema.targetSchema.displayValue]}
            </span>
        );
    }
});

const coloredCell = React.memo(({value, object}) => {
    if (value) {
        return (
            <span style={{ fontSize: '8pt', color: getCellColor(object) }}>{value}</span>
        );
    }
});

export function useActivityLogsResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('submittedDate', SortDirections.DESCENDING);

    const columns = useMemo(() => ([
        { id: 'status',        accessor: ActivityLogsGetListSchema.status,        header: '',                                width: 32,  align: 'center', name: t('SMC_LOGSTASKS_TABLE_STATUS'), defaultShow: true, sort: buildWeighedSort(ActivityLogsGetListSchema.status, statusWeights),       Cell: statusCell  },
        { id: 'submittedDate', accessor: ActivityLogsGetListSchema.submittedDate, header: t('SMC_LOGSTASKS_TABLE_SUBMITED'), width: 130,                                                         defaultShow: true, sort: buildNumberSort(ActivityLogsGetListSchema.submittedDate),                Cell: dateCell    },
        { id: 'target',        accessor: ActivityLogsGetListSchema.target,        header: t('SMC_LOGSTASKS_TABLE_TARGET'),   width: 100,                                                         defaultShow: true, sort: buildStringSortCaseInsensitive(ActivityLogsGetListSchema.target),        Cell: targetCell  },
        { id: 'serviceDomain', accessor: ActivityLogsGetListSchema.serviceDomain, header: t('SMC_LOGSTASKS_TABLE_PORTAL'),   width: 150,                                                         defaultShow: true, sort: buildStringSortCaseInsensitive(ActivityLogsGetListSchema.serviceDomain), Cell: coloredCell },
        { id: 'action',        accessor: ActivityLogsGetListSchema.action,        header: t('SMC_LOGSTASKS_TABLE_ACTION'),   width: 160,                                                         defaultShow: true, sort: buildStringSortCaseInsensitive(ActivityLogsGetListSchema.action),        Cell: coloredCell },
        { id: 'details',       accessor: ActivityLogsGetListSchema.details,       header: t('SMC_LOGSTASKS_TABLE_MSG'),      width: 400,                                                         defaultShow: true, sort: buildStringSortCaseInsensitive(ActivityLogsGetListSchema.details),       Cell: coloredCell }
    ]), [t]);

    const rowIdSelector = (obj, index) => index;

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.ACTIVITY_LOGS, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.ACTIVITY_LOGS, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        selectedObjects,
        setSelectedObjects
    } = useGenericResultsDataApi(ObjectTypes.ACTIVITY_LOGS);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const clearFinished = useCallback(() => {
        Api.clearFinishedTasksFromActivityLogs()
            .then(() => {
                reloadButtonOnClick();
            })
            .catch(e => {
                const errorMessage = getErrorMessage(e);
                enqueueErrorSnackbar(errorMessage);
            });
    }, [reloadButtonOnClick, enqueueErrorSnackbar, getErrorMessage]);

    const cancelSelected = useCallback(() => {
        if (!selectedObjects || !selectedObjects.length) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('SMC_LOGSTASKS_CANCEL_QUES'))) {
            Api.cancelTasks(selectedObjects)
                .then(() => {
                    enqueueSuccessSnackbar(t('SMC_LOGSTASKS_DONE'));
                    reloadButtonOnClick();
                })
                .catch(e => {
                    const errorMessage = getErrorMessage(e);
                    enqueueErrorSnackbar(errorMessage);
                });
        }
    }, [selectedObjects, reloadButtonOnClick, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    const toExcelAction = useCallback(() => {
        enqueueSnackbar(null, {
            persist: true,
            content: (key) => (
                <LogsToExcelSnackbarContent
                    objectType={ObjectTypes.ACTIVITY_LOGS}
                    id={key}
                />
            )
        })
    }, [enqueueSnackbar]);

    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        reloadButtonOnClick,
        cancelSelected,
        clearFinished,
        toExcelAction
    });
}